import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControlLabel,
  Grid,
  Paper,
  Slider,
  Switch,
  Typography,
} from '@mui/material';
import { MSME_PRICING_CONTACT_EMAIL, formatInr } from '../constants/pricingPlans';
import { estimateRecommendedPlan } from '../utils/pricingEstimator';

interface PricingEstimatorProps {
  contactOnly?: boolean;
  contactEmail?: string;
}

const PricingEstimator: React.FC<PricingEstimatorProps> = ({
  contactOnly = false,
  contactEmail = MSME_PRICING_CONTACT_EMAIL,
}) => {
  const [documentsPerMonth, setDocumentsPerMonth] = useState(40);
  const [sites, setSites] = useState(1);
  const [users, setUsers] = useState(2);
  const [needsCompliance, setNeedsCompliance] = useState(false);

  const estimate = useMemo(
    () =>
      estimateRecommendedPlan({
        documentsPerMonth,
        sites,
        users,
        needsCompliance,
      }),
    [documentsPerMonth, sites, users, needsCompliance]
  );

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Plan estimator
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
        {contactOnly
          ? `Adjust your expected usage to see a suggested tier. Email ${contactEmail} for pricing tailored to your volume.`
          : 'Adjust your expected usage to see a suggested tier. Prices are indicative monthly amounts excl. GST; overage is ₹12 per document above plan limits.'}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" gutterBottom>
            Documents per month: {documentsPerMonth}
          </Typography>
          <Slider
            value={documentsPerMonth}
            min={0}
            max={400}
            step={5}
            valueLabelDisplay="auto"
            onChange={(_, v) => setDocumentsPerMonth(v as number)}
            aria-label="Documents per month"
          />

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Sites: {sites}
          </Typography>
          <Slider
            value={sites}
            min={1}
            max={5}
            step={1}
            marks
            valueLabelDisplay="auto"
            onChange={(_, v) => setSites(v as number)}
            aria-label="Number of sites"
          />

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Users: {users}
          </Typography>
          <Slider
            value={users}
            min={1}
            max={15}
            step={1}
            valueLabelDisplay="auto"
            onChange={(_, v) => setUsers(v as number)}
            aria-label="Number of users"
          />

          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                checked={needsCompliance}
                onChange={(e) => setNeedsCompliance(e.target.checked)}
                color="primary"
              />
            }
            label="I need BRSR / compliance hub and disclosure exports"
          />
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.12)' : 'rgba(76, 175, 80, 0.08)',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="overline" color="primary">
              Suggested tier
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, textTransform: 'capitalize', mb: 1 }}>
              {estimate.recommendedTier}
            </Typography>
            {contactOnly ? (
              estimate.recommendedTier === 'free' ? (
                <Typography variant="h4" color="primary.main" sx={{ fontWeight: 800 }}>
                  Free
                </Typography>
              ) : (
                <Typography variant="h5" color="primary.main" sx={{ fontWeight: 800 }}>
                  Email {contactEmail} for pricing
                </Typography>
              )
            ) : (
              <>
                <Typography variant="h4" color="primary.main" sx={{ fontWeight: 800 }}>
                  {estimate.monthlyAmountInr > 0
                    ? `${formatInr(estimate.monthlyAmountInr)}/mo`
                    : 'Free'}
                </Typography>
                {estimate.monthlyAmountInclGst > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    ≈ {formatInr(estimate.monthlyAmountInclGst)} incl. 18% GST
                  </Typography>
                )}
                {estimate.overageCostInr > 0 && (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
                    Includes ~{formatInr(estimate.overageCostInr)} estimated document overage
                  </Typography>
                )}
              </>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.55 }}>
              {estimate.rationale}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PricingEstimator;
